import { cn } from 'cn';
import { NavLink } from 'react-router';
import { useAllowed } from '@/auth/permissions';
import { useSession } from '@/auth/use-session';
import { NAV_ITEMS, type NavItem } from './navigation';

const GROUPS: (NavItem['group'] | undefined)[] = [
  undefined,
  'Outreach',
  'Sending',
  'Insights',
  'Customers',
  'Platform',
  'Account',
];

/**
 * The menu. Collapsed (icons only), each item keeps its place and its name (faded out, so links
 * keep their names for screen readers and tests); a section title becomes a thin line of the same
 * height, so nothing jumps while the sidebar opens.
 */
export function SidebarNav({
  onNavigate,
  expanded = true,
  items = NAV_ITEMS,
}: {
  onNavigate?: (() => void) | undefined;
  /** False = the icon rail of the collapsed sidebar. */
  expanded?: boolean;
  /** The organisation's menu, or Super Admin's (Step 17e). */
  items?: NavItem[];
}) {
  const allowed = useAllowed();
  const session = useSession();
  const visible = items.filter(
    (item) =>
      (!item.permission || allowed(item.permission)) &&
      (!item.platformAdmin || session.isPlatformAdmin) &&
      (!item.platformStaff || session.platformRole !== null),
  );
  const fade = cn(
    'transition-opacity duration-150 motion-reduce:transition-none',
    expanded ? 'opacity-100 delay-75' : 'opacity-0',
  );
  return (
    <nav aria-label="Main" className="grid grid-cols-[minmax(0,1fr)] gap-1">
      {/* A group without any item this person can see shows no heading either. */}
      {GROUPS.filter((group) => visible.some((item) => item.group === group)).map((group) => (
        <div key={group ?? 'top'} className="grid grid-cols-[minmax(0,1fr)] gap-0.5">
          {group && (
            <div className="flex h-7 items-center px-[14px]" aria-hidden={!expanded || undefined}>
              {expanded ? (
                <p
                  className={cn(
                    'text-[11px] font-semibold tracking-wider whitespace-nowrap text-muted-foreground/80 uppercase',
                    fade,
                  )}
                >
                  {group}
                </p>
              ) : (
                <span className="h-px w-full bg-sidebar-border" />
              )}
            </div>
          )}
          {visible
            .filter((item) => item.group === group)
            .map(({ to, label, icon: Icon, comingSoon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/home' || to === '/admin'}
                onClick={onNavigate}
                title={expanded ? undefined : label}
                className={({ isActive }) =>
                  cn(
                    'group/item relative flex h-10 min-w-0 items-center gap-3 rounded-lg px-[14px] text-sm font-medium whitespace-nowrap outline-none',
                    'transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring/60',
                    isActive
                      ? 'bg-primary/10 text-foreground'
                      : 'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-foreground',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span
                        aria-hidden="true"
                        className="absolute top-2 bottom-2 -left-3 w-[3px] rounded-r-full bg-primary"
                      />
                    )}
                    <Icon
                      aria-hidden="true"
                      strokeWidth={1.9}
                      className={cn(
                        'size-5 shrink-0 transition-colors',
                        isActive
                          ? 'text-primary'
                          : 'text-muted-foreground group-hover/item:text-foreground',
                      )}
                    />
                    <span className={cn('flex-1 truncate', fade)}>{label}</span>
                    {comingSoon && (
                      <span
                        className={cn(
                          'rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground',
                          fade,
                        )}
                      >
                        Soon
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
        </div>
      ))}
    </nav>
  );
}
